import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Popover,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Feed } from "../types";
import { ACCENT, surface, text } from "../theme";
import { SourceAvatar } from "./SourceAvatar";

interface Props {
  feeds: Feed[];
  selected: string[];
  onChange: (feedIds: string[]) => void;
}

/** Sources with nothing published in this long are flagged as dormant. */
const DORMANT_AFTER_DAYS = 120;

function isDormant(feed: Feed): boolean {
  if (!feed.healthy) return true;
  if (!feed.newestItem) return false;
  const newest = Date.parse(feed.newestItem);
  if (!Number.isFinite(newest)) return false;
  return Date.now() - newest > DORMANT_AFTER_DAYS * 86400000;
}

/**
 * Maximum height of the source list, in pixels.
 *
 * Deliberately not a `vh` value: when the reader is embedded, the iframe is
 * sized to its full content height, so the iframe's viewport is the whole
 * document and `vh` resolves to thousands of pixels.
 */
const LIST_MAX_HEIGHT = 340;

/**
 * Source filter: an always-visible chip row for quick toggling, plus a panel
 * for bulk selection. The original build hid all filtering behind a dialog,
 * so which sources were active was never visible on the page.
 *
 * The panel is a Popover anchored to the Manage button rather than a centred
 * Dialog. A Dialog is `position: fixed` and centres itself in the viewport —
 * which, inside the auto-sized embed iframe, is the entire document. That put
 * the dialog thousands of pixels below the reader while its backdrop covered
 * everything, so the embed just dimmed and showed nothing. Anchoring resolves
 * against the same rect as the button that was just clicked, so the panel
 * always opens where the reader is looking, framed or not.
 */
export function SourceFilter({ feeds, selected, onChange }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const allSelected = selected.length === feeds.length && feeds.length > 0;

  const toggle = (feedId: string) => {
    onChange(
      selected.includes(feedId)
        ? selected.filter((id) => id !== feedId)
        : [...selected, feedId]
    );
  };

  const sorted = useMemo(
    () => [...feeds].sort((a, b) => b.itemCount - a.itemCount),
    [feeds]
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        role="group"
        aria-label="Filter by source"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flex: 1,
          minWidth: 0,
          // Always one scrollable row. The bar pins to the top of the
          // viewport, so wrapping 15 chips would eat a large slice of every
          // screen; "Manage" is there for seeing them all at once.
          flexWrap: "nowrap",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& > *": { flexShrink: 0 },
          // Bleed to the container edge so the strip reads as scrollable.
          ml: { xs: -2, md: 0 },
          pl: { xs: 2, md: 0 },
        }}
      >
        <Chip
          label="All sources"
          size="small"
          onClick={() => onChange(feeds.map((feed) => feed.id))}
          variant={allSelected ? "filled" : "outlined"}
          sx={{
            backgroundColor: allSelected ? ACCENT : "transparent",
            color: allSelected ? "#1A1008" : text.secondary,
            fontWeight: 650,
            "&:hover": {
              backgroundColor: allSelected ? ACCENT : surface.raised,
            },
          }}
        />

        {sorted.map((feed) => {
          const active = selected.includes(feed.id);
          return (
            <Chip
              key={feed.id}
              size="small"
              label={feed.title}
              onClick={() => toggle(feed.id)}
              aria-pressed={active}
              icon={
                <Box sx={{ display: "flex", ml: 0.75 }}>
                  <SourceAvatar
                    title={feed.title}
                    image={feed.defaultImage}
                    size={15}
                  />
                </Box>
              }
              variant={active && !allSelected ? "filled" : "outlined"}
              sx={{
                color: active ? text.primary : text.tertiary,
                borderColor: active ? surface.borderStrong : surface.border,
                backgroundColor:
                  active && !allSelected ? surface.raised : "transparent",
                opacity: active ? 1 : 0.6,
                "&:hover": { backgroundColor: surface.raised, opacity: 1 },
              }}
            />
          );
        })}
      </Box>

      {/* Outside the scroll strip: on a phone the strip scrolls sideways, and a
          button inside it can sit off-screen — taking the anchored panel with
          it. Keeping it here means it is always reachable. */}
      <Button
        size="small"
        startIcon={<TuneRoundedIcon sx={{ fontSize: 16 }} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ color: ACCENT, flexShrink: 0 }}
      >
        Manage
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        marginThreshold={8}
        // Locking body scroll would change the document height, which the
        // embed reports to its host — the iframe would resize while the panel
        // is open.
        disableScrollLock
        // No grow animation. The panel starts at opacity 0 and animates in, and
        // CSS transitions do not advance in an iframe the browser has throttled
        // — which would leave the panel permanently invisible in the embed. A
        // filter list gains nothing from the animation anyway.
        transitionDuration={0}
        slotProps={{
          paper: {
            "aria-label": "Sources",
            sx: { width: 320, maxWidth: "calc(100vw - 16px)", mt: 0.5 },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
          <Typography variant="h3" sx={{ color: text.primary }}>
            Sources
          </Typography>
          <Typography variant="caption" sx={{ color: text.tertiary }}>
            {selected.length} of {feeds.length} selected
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mt: 1.25 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onChange(feeds.map((feed) => feed.id))}
              sx={{ color: ACCENT, borderColor: surface.borderStrong }}
            >
              Select all
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onChange([])}
              sx={{ color: text.secondary, borderColor: surface.borderStrong }}
            >
              Clear
            </Button>
          </Box>
        </Box>

        <Divider sx={{ borderColor: surface.border }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: LIST_MAX_HEIGHT,
            overflowY: "auto",
            px: 1,
            py: 0.5,
          }}
        >
          {sorted.map((feed) => {
            const dormant = isDormant(feed);
            return (
              <Box
                key={feed.id}
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 0.5,
                  pr: 1,
                  cursor: "pointer",
                  borderRadius: 1,
                  "&:hover": { backgroundColor: surface.raised },
                }}
              >
                <Switch
                  size="small"
                  checked={selected.includes(feed.id)}
                  onChange={() => toggle(feed.id)}
                  inputProps={{ "aria-label": feed.title }}
                />
                <SourceAvatar
                  title={feed.title}
                  image={feed.defaultImage}
                  size={22}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: text.primary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {feed.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: text.tertiary }}>
                    {feed.itemCount} item{feed.itemCount === 1 ? "" : "s"}
                  </Typography>
                </Box>
                {dormant && (
                  <Tooltip
                    title={
                      feed.healthy
                        ? "No recent posts from this source"
                        : "This source is not responding"
                    }
                  >
                    <WarningAmberRoundedIcon
                      sx={{ fontSize: 16, color: "#C9A227", flexShrink: 0 }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
}
